/**
 * career-opportunity-detail — the entity surface behind `/search/opportunities/:id`.
 *
 * Binds via the `urn` attribute (the router sets it from the codec; listed in
 * `entitySurfaces`, so it MUST carry a valid urn). Deep-linkable COLD: `attach()`
 * hydrates from `/api/opportunities/:id` (+ `/api/organizations/:orgRef` when the
 * posting carries an org) into the opportunity + org stores, then binds the folded
 * views.
 *
 * Presentation (§0 seamless document over cards): ONE editorial document — eyebrow +
 * serif title + employer + the quiet verify reading in the head; the match verdict as
 * the left-accent HERO (tier word + score + meter — the one thing the person came to
 * learn); then hairline sections: the authored brief, the per-dimension signals
 * (strength + verbatim quotes + named gaps, AX-6), and the employer dossier — each
 * with its provenance chip + honest `is-degraded` cue. No `.surface-panel` boxes.
 * Bookmark / dismiss / delete stay the same optimistic store commands as the list.
 * Card CONTENT is domain data, rendered as authored.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import {
  I18nToken,
  OpportunityStoreToken,
  OrgStoreToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
} from "../services"
import { createStreamClaim, type StreamClaim } from "../lib/push-streams"
import { writeLastGeneration } from "../lib/last-generation"
import type { CareerI18n } from "../../i18n"
import {
  careerStream,
  orgUrn,
  type MatchSignalView,
  type OpportunityCardView,
  type OpportunityStore,
  type OrgStore,
  type OrgSummaryView,
  type ShellSession,
} from "../domain"
import {
  DIMENSION_KEY,
  JOB_STATE_KEY,
  STAGE_KEY,
  bandForStrength,
  opportunityShortId,
  pct,
  tagHint,
  tagLabel,
  tierReadout,
} from "../lib/opportunity-card"
import { signalTags } from "../lib/opportunity-pipeline"

interface OpportunityWithMatch {
  opportunity: Record<string, unknown>
  match: Record<string, unknown> | null
}
/** What `POST /api/applications/generate` answers with (S11). */
interface GenerateResponse {
  applicationId: string
  generationId: string
  streamId: string
  applicationStreamId: string
}
interface OrgWithSummary {
  org: Record<string, unknown> | null
  summary: Record<string, unknown> | null
}

export class OpportunityDetailVm implements ViewModel {
  readonly card: ReadonlySignal<OpportunityCardView | undefined>
  readonly org: Signal<OrgSummaryView | null> = signal<OrgSummaryView | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  /** An application generation started from this page (S11) is in flight. */
  readonly generating: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  readonly urn: Urn
  private readonly shortId: string
  private seq = 1

  /** This surface's claim on the shared push channel (`lib/push-streams.ts`): the channel
   *  subscription is the UNION of every mounted surface's claim, never just ours. */
  private readonly claim: StreamClaim

  constructor(
    private readonly store: OpportunityStore,
    private readonly orgStore: OrgStore,
    push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
    urn: Urn,
  ) {
    this.t = i18n.t
    this.claim = createStreamClaim(push)
    this.urn = urn
    this.shortId = opportunityShortId(urn)
    this.card = store.entity(urn)
    this.renderDeps = [this.card, this.org, this.error, this.loaded, this.generating, store.pending]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    return () => this.claim.release()
  }

  bookmark(): void {
    void this.store.command({ type: "bookmark", payload: { opportunityId: this.urn } })
  }
  dismiss(): void {
    void this.store.command({ type: "dismiss", payload: { opportunityId: this.urn } })
  }
  remove(): void {
    void this.store.command({ type: "delete", payload: { opportunityId: this.urn } })
  }
  /**
   * Start an application for THIS Stelle (S11) — the natural place to offer it, since the
   * person has just read the brief and the match. Identical call shape to the review surface's
   * retry: POST → remember both streams → subscribe → navigate, so the review page's rail
   * plays from the first event instead of from whenever it mounts.
   */
  async generateApplication(): Promise<void> {
    if (this.generating.value) return
    this.generating.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<GenerateResponse>("/api/applications/generate", { opportunityId: this.shortId })
      writeLastGeneration(this.userId, { streamId: res.streamId, applicationStreamId: res.applicationStreamId })
      this.claim.claim([res.streamId, res.applicationStreamId])
      void this.router.navigate(`/applications/${res.applicationId.replace(/^urn:career:application:/, "")}`)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.generating.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const { opportunity, match } = await apiGet<OpportunityWithMatch>(`/api/opportunities/${this.shortId}`)
      if (userId && opportunity && typeof opportunity["id"] === "string") {
        const streamId = careerStream(userId, "Opportunity", opportunity["id"])
        this.store.apply({ streamId, version: this.seq++, type: "opportunity.Discovered", payload: { owner: userId, opportunity } })
        if (match) {
          this.store.apply({ streamId, version: this.seq++, type: "opportunity.Matched", payload: { owner: userId, opportunityId: opportunity["id"], match } })
        }
        const orgRef = opportunity["orgRef"]
        if (typeof orgRef === "string" && orgRef.length > 0) await this.hydrateOrg(userId, orgRef)
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  private async hydrateOrg(userId: string, orgRef: string): Promise<void> {
    try {
      const { org, summary } = await apiGet<OrgWithSummary>(`/api/organizations/${encodeURIComponent(orgRef)}`)
      if (!org) return
      const streamId = careerStream(userId, "Organization", orgRef)
      this.orgStore.apply({
        streamId,
        version: this.seq++,
        type: "organization.Observed",
        payload: { owner: userId, orgId: orgRef, name: org["name"] ?? "", evidence: org["evidence"] ?? [], observedAt: org["firstSeenAt"] ?? "" },
      })
      this.orgStore.apply({
        streamId,
        version: this.seq++,
        type: "organization.Researched",
        payload: { owner: userId, evidence: org["evidence"] ?? [], ...(org["register"] ? { register: org["register"] } : {}) },
      })
      if (summary) {
        this.orgStore.apply({ streamId, version: this.seq++, type: "organization.Summarized", payload: { owner: userId, summary } })
      }
      this.org.set(this.orgStore.entity(orgUrn(orgRef)).value ?? null)
    } catch {
      // org dossier is optional — leave the section empty on failure.
    }
  }
}

export class CareerOpportunityDetail extends KitElement<OpportunityDetailVm> {
  protected createViewModel(ctx: KitContext): OpportunityDetailVm {
    const { urn } = this
    if (!urn) throw new Error("[career] <career-opportunity-detail> needs a urn attribute.")
    return new OpportunityDetailVm(
      ctx.inject(OpportunityStoreToken),
      ctx.inject(OrgStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
      urn,
    )
  }

  /**
   * One per-dimension criterion (S9c): name + strength + a meter carrying THE SAME four-band
   * ramp as the overall score (via the domain's `tierForScore` thresholds — one threshold
   * table, not two), then BULLET POINTS. Evidence and gaps share one list and are told apart
   * by their mark; the gaps used to be a comma-joined prose line, which read as a sentence and
   * hid how many there were.
   */
  private matchSignal(vm: OpportunityDetailVm, s: MatchSignalView): TemplateResult {
    const label = DIMENSION_KEY[s.dimension] ?? "opportunity.dimension.fit"
    const strength = pct(s.strength)
    const band = bandForStrength(s.strength)
    return html`
      <li class=${classMap({ "match-signal": true, [`opp-tier--${band}`]: true })}>
        <p class="match-dimension" lang="de">
          <span class="match-dimension-name">${vm.t(label)}</span>
          <span class="match-strength" lang="de">${vm.t("opportunity.match.strength", { n: strength })}</span>
        </p>
        <span class="match-meter" aria-hidden="true"><i style=${`width:${strength}%`}></i></span>
        ${s.quotes.length > 0 || s.gaps.length > 0
          ? html`<ul class="match-points" role="list">
              ${s.quotes.map((q) => html`<li class="match-point match-point--quote" lang="de">„${q}“</li>`)}
              ${s.gaps.map(
                (g) => html`<li class="match-point match-point--gap" lang="de">
                  <span class="match-point-label">${vm.t("opportunity.match.gapMark")}</span>${g}
                </li>`,
              )}
            </ul>`
          : nothing}
      </li>
    `
  }

  /**
   * The warnings, spelled out (S9b). On the card these are five-word labels with a hover; this
   * is the page with room for the whole sentence, so the same tag vocabulary is rendered here
   * as label + explanation. Nothing new is computed — it is the identical `signalTags` call.
   */
  private flagSection(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    const warnings = signalTags(o).filter((tag) => tag.kind === "warn")
    if (warnings.length === 0) return nothing
    return html`
      <section class="ws-section opportunity-flags" aria-labelledby="opportunity-flags-title">
        <header class="ws-sec-head">
          <h2 id="opportunity-flags-title" class="ws-sec-label" lang="de">${vm.t("opportunity.flags.title")}</h2>
        </header>
        <ul class="opp-flags" role="list">
          ${warnings.map(
            (tag) => html`<li class="opp-flag">
              <span class="opp-tag opp-tag--warn" data-tag=${tag.code} lang="de">${tagLabel(tag, vm.t)}</span>
              <span class="opp-flag-text" lang="de">${tagHint(tag, vm.t)}</span>
            </li>`,
          )}
        </ul>
      </section>
    `
  }

  private matchSection(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    if (o.signals.length === 0) return nothing
    return html`
      <section class="ws-section opportunity-match" aria-labelledby="opportunity-match-title">
        <header class="ws-sec-head">
          <h2 id="opportunity-match-title" class="ws-sec-label" lang="de">${vm.t("opportunity.match.title")}</h2>
        </header>
        <ul class="match-signals" role="list">${o.signals.map((s) => this.matchSignal(vm, s))}</ul>
      </section>
    `
  }

  private briefSection(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    if (!o.brief) return nothing
    const b = o.brief
    return html`
      <section class=${classMap({ "ws-section": true, "opportunity-brief": true, "is-degraded": b.degraded })} aria-labelledby="opportunity-brief-title">
        <header class="ws-sec-head">
          <!-- S9c: the ONE coherent formalized text on this page. Every other criterion below
               is bullet points; this is the overall assessment, and it says so. -->
          <h2 id="opportunity-brief-title" class="ws-sec-label" lang="de">${vm.t("opportunity.assessment.title")}</h2>
        </header>
        ${b.degraded ? html`<p class="opportunity-degraded-cue" role="status" lang="de">${vm.t("opportunity.brief.degraded")}</p>` : nothing}
        <p class="opportunity-brief-text ws-prose" lang="de">${b.brief}</p>
        ${b.fitNote ? html`<p class="opportunity-fitnote" lang="de">${b.fitNote}</p>` : nothing}
        <p class="opportunity-brief-meta">
          <span class="opportunity-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: b.provenanceCount })}</span>
        </p>
      </section>
    `
  }

  private orgSection(vm: OpportunityDetailVm): TemplateResult {
    const org = vm.org.value
    return html`
      <section class=${classMap({ "ws-section": true, "opportunity-org": true, "is-degraded": Boolean(org?.degraded) })} aria-labelledby="opportunity-org-title">
        <header class="ws-sec-head">
          <h2 id="opportunity-org-title" class="ws-sec-label" lang="de">${vm.t("org.summary.title")}</h2>
        </header>
        ${org?.summaryBody
          ? html`
              ${org.name ? html`<p class="opportunity-org-name" lang="de">${org.name}</p>` : nothing}
              <p class="opportunity-org-summary ws-prose" lang="de">${org.summaryBody}</p>
              <p class="opportunity-org-meta">
                <span class="opportunity-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: org.summaryProvenanceCount })}</span>
                ${org.evidenceCount > 0 ? html`<span class="opportunity-org-evidence chip" lang="de">${vm.t("org.evidence", { n: org.evidenceCount })}</span>` : nothing}
              </p>
            `
          : html`<p class="surface-empty" lang="de">${vm.t("org.summary.empty")}</p>`}
      </section>
    `
  }

  /**
   * The verify reading — the FACTS only: job state and salary. The fit word is gone from here
   * as it is from the card; `passt` merely restated the score, and `nicht eindeutig` / `passt
   * nicht` now carry their own explanation in the warnings section below.
   */
  private verifyLine(vm: OpportunityDetailVm, o: OpportunityCardView): TemplateResult | typeof nothing {
    if (!o.jobState && !o.salary) return nothing
    const parts: TemplateResult[] = []
    if (o.jobState) parts.push(html`<span class="opp-jobstate">${vm.t(JOB_STATE_KEY[o.jobState])}</span>`)
    if (o.salary) parts.push(html`<span class="opp-salary">${vm.t("opportunity.salary")}: ${o.salary}</span>`)
    return html`<p class="opp-verify" lang="de">${parts.map((p, i) => (i === 0 ? p : html`<span class="opp-dot" aria-hidden="true"> · </span>${p}`))}</p>`
  }

  protected render(vm: OpportunityDetailVm): TemplateResult {
    const o = vm.card.value
    const back = html`<a class="opportunity-back" href="/search" data-act="nav" data-url="/search" lang="de">${vm.t("opportunity.detail.back")}</a>`

    if (!o || o.dismissed) {
      // Removed/dismissed OR not yet loaded → an honest state (still a valid AX tree).
      const gone = (o?.dismissed ?? false) || (vm.loaded.value && !o)
      return html`
        <section class="surface opportunity-detail" urn=${vm.urn} aria-labelledby="opportunity-detail-title">
          <div class="ws-doc">
            <h1 id="opportunity-detail-title" class="surface-title" lang="de">${vm.t("surface.opportunity.title")}</h1>
            ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
            ${gone ? html`<p class="surface-empty" lang="de">${o?.dismissed ? vm.t("opportunity.detail.removed") : vm.t("opportunity.detail.notFound")}</p>` : nothing}
            ${back}
          </div>
        </section>
      `
    }

    return html`
      <section
        class=${classMap({ surface: true, "opportunity-detail": true, [`is-${o.stage}`]: true, "is-bookmarked": o.bookmarked })}
        urn=${vm.urn}
        aria-labelledby="opportunity-detail-title"
      >
        <div class="ws-doc opp-doc">
          <header class="kb-doc-head opportunity-detail-head">
            <p class="eyebrow" lang="de">${vm.t("opportunity.detail.eyebrow")} · <span class="opportunity-stage" lang="de">${vm.t(STAGE_KEY[o.stage])}</span></p>
            <h1 id="opportunity-detail-title" class="surface-title kb-doc-title" lang="de">${o.title}</h1>
            <!-- P-B: the HIRING company is the identity; the board it was found on is a quiet
                 attribution, honest now that the employer field is no longer the board's host. -->
            <p class="opportunity-employer" lang="de">
              ${o.company?.name ?? o.employer}${o.location ? html` · ${o.location}` : nothing}${o.sourcePlatform
                ? html` · <span class="opp-company-via">${vm.t("search.company.via", { platform: o.sourcePlatform })}</span>`
                : nothing}
            </p>
            ${this.verifyLine(vm, o)}
            ${o.url ? html`<a class="opportunity-posting-link" href=${o.url} target="_blank" rel="noopener noreferrer" lang="de">${vm.t("opportunity.posting.link")}</a>` : nothing}
          </header>

          <!-- The band class rides the HERO, not only the readout inside it: the tier-hue
               variable is declared on the opp-tier modifier, and a parent cannot read a
               child's custom property — without this the accent bar stayed grey while the
               meter beside it was coloured. -->
          ${o.tier
            ? html`<div
                class=${classMap({ "opp-hero": true, [`opp-tier--${o.tier}`]: true })}
                role="group"
                aria-label=${vm.t("opportunity.match.title")}
              >
                ${tierReadout(o, vm.t)}
              </div>`
            : nothing}

          ${this.briefSection(vm, o)}
          ${this.flagSection(vm, o)}
          ${this.matchSection(vm, o)}
          ${this.orgSection(vm)}

          <div class="opportunity-actions" role="group">
            ${o.bookmarked
              ? html`<span class="opp-saved" lang="de">${vm.t("opportunity.bookmarked")}</span>`
              : html`<button class="opp-act" data-act="bookmark" data-opportunity=${o.urn} lang="de">${vm.t("opportunity.bookmark")}</button>`}
            <button class="opp-act" data-act="dismiss" data-opportunity=${o.urn} lang="de">${vm.t("opportunity.dismiss")}</button>
            <button class="opp-act opp-act--remove" data-act="delete" data-opportunity=${o.urn} lang="de">${vm.t("opportunity.delete")}</button>
            <!-- S11: generate from the detail — gated on bookmarked for the same reason as the
                 card, the save is the verdict that precedes the spend. -->
            ${o.bookmarked
              ? html`<button class="opp-act opp-act--generate" data-act="generate-application" data-opportunity=${o.urn} lang="de">
                  ${vm.generating.value ? vm.t("opportunity.generating") : vm.t("opportunity.generate")}
                </button>`
              : nothing}
          </div>
          ${back}
        </div>
      </section>
    `
  }

  protected override actions(vm: OpportunityDetailVm): ActionMap {
    return {
      "generate-application": () => void vm.generateApplication(),
      bookmark: () => vm.bookmark(),
      dismiss: () => vm.dismiss(),
      delete: () => vm.remove(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
